import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Map specific Question IDs to their image assets
const IMAGE_MAP: Record<string, string> = {
  // Creator
  'q1_leverage': '/images/illustrations/quiz/creator_q1_leverage.avif',
  'q2_infrastructure': '/images/illustrations/quiz/creator_q2_infrastructure.avif',
  'q3_capital': '/images/illustrations/quiz/creator_q3_capital.avif',
  
  // Transformer
  'q1_signal': '/images/illustrations/quiz/transformer_q1_signal.avif',
  'q2_priority': '/images/illustrations/quiz/transformer_q2_bottleneck.avif',
  'q3_memory': '/images/illustrations/quiz/transformer_q3_memory.avif',
  'q4_governance': '/images/illustrations/quiz/transformer_q4_governance.avif',

  // Builder (Branch A - Existing)
  'q1_context': '/images/illustrations/quiz/builder_q1_context.avif',
  'q2a_integration': '/images/illustrations/quiz/builder_q2_connection.avif',
  'q3a_ux': '/images/illustrations/quiz/builder_q3_ux.avif',

  // Builder (Branch B - New)
  'q2b_core_value': '/images/illustrations/quiz/builder_q2_core_value.avif',
  'q3b_complexity': '/images/illustrations/quiz/builder_q3_complexity.avif',
};

const getFallbackImage = (trackId: string) => {
  if (trackId === 'creator') return '/images/illustrations/rocket-engine.avif';
  if (trackId === 'transformer') return '/images/illustrations/factory-gear.avif';
  if (trackId === 'builder') return '/images/illustrations/app-layer.avif';
  return '/images/illustrations/quiz/default-placeholder.avif';
};

export const VisualContext: React.FC<{ questionId: string; trackId: string; className?: string }> = ({ questionId, trackId, className }) => {
  const imageSrc = IMAGE_MAP[questionId] || getFallbackImage(trackId);

  return (
    <div className={`w-full flex items-center justify-center relative overflow-hidden bg-slate-50 
      ${/* MOBILE FIX: Use fixed height (h-[220px]) instead of aspect-square to save vertical space. */''}
      h-[220px] md:h-full md:aspect-auto
      ${className}`}>
      
      {/* Background Grid Pattern */}
      <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#000_1px,transparent_1px)] [background-size:20px_20px]" />

      <AnimatePresence mode="wait">
        <motion.div
          key={questionId}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 1.05 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          // Reduced padding on mobile to maximize image size within the smaller container
          className="relative z-10 w-full h-full flex items-center justify-center p-4 md:p-8"
        >
          <img 
            src={imageSrc}
            alt="Visual Context"
            className="w-full h-full object-contain drop-shadow-2xl"
            onError={(e) => {
                e.currentTarget.src = getFallbackImage(trackId);
            }}
          />
        </motion.div>
      </AnimatePresence>
    </div>
  );
};